import { useEffect, useRef } from 'react';
import type { Workspace } from '@workspace/domain/workspace';
import { useSidebarWorkspace } from './useSidebarWorkspace';
import { WorkspaceItem } from '../items';
import { SidebarTreeViewModelItemContainer } from './SidebarTreeViewModelItemContainer';
import { SidebarTreeFolderItemContainer } from './SidebarTreeFolderItemContainer';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';

type Props = {
  workspace: Workspace;
  rootFolderTree: RootFolderTree;
  shouldBeScrolled?: boolean;
};

export const SidebarTreeWorkspaceItemContainer: React.FC<Props> = ({
  workspace,
  rootFolderTree,
  shouldBeScrolled = false,
}: Props) => {
  const {
    firstLoaded,
    sortedFolders,
    sortedViewModels,
    isOpen,
    toggleWorkspace,
    isSelected,
    resetScrollWorkspace,
    resetScrollViewModel,
    scrolledViewModelId,
    scrolledFolderId,
    sidebarTreeContainerRef,
  } = useSidebarWorkspace(workspace.id, rootFolderTree);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldBeScrolled && ref.current && sidebarTreeContainerRef?.current) {
      const rect = ref.current.getBoundingClientRect();
      const treeContainer = sidebarTreeContainerRef.current;
      const { top: treeContainerTop, bottom: treeContainerBottom } = treeContainer.getBoundingClientRect();
      if (rect.top <= treeContainerTop || treeContainerBottom <= rect.bottom) {
        ref.current.scrollIntoView();
      }
      resetScrollWorkspace();
    }
  }, [shouldBeScrolled, ref, resetScrollWorkspace, sidebarTreeContainerRef]);

  return (
    <WorkspaceItem
      ref={ref}
      workspace={workspace}
      isOpen={isOpen}
      toggleWorkspace={toggleWorkspace}
      isSelected={isSelected}
      firstLoaded={firstLoaded}
    >
      <>
        {sortedFolders.map((folder) => (
          <SidebarTreeFolderItemContainer
            key={folder.id}
            workspace={workspace}
            folder={folder}
            rootFolderTree={rootFolderTree}
            shouldBeScrolled={scrolledFolderId === folder.id}
          />
        ))}
        {sortedViewModels.map((viewModel) => (
          <SidebarTreeViewModelItemContainer
            key={viewModel.id}
            workspace={workspace}
            viewModel={viewModel}
            shouldBeScrolled={scrolledViewModelId === viewModel.id}
            resetScrollViewModel={resetScrollViewModel}
          />
        ))}
      </>
    </WorkspaceItem>
  );
};
